import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "./useOrg";
import { getSupportView, listStaffOrganisations, StaffOrganisation } from "../services/supportView";

/**
 * True when the signed-in person may look but not change anything: Telcofy
 * support viewing a customer (#557), or a viewer in their organisation (#554).
 * Edit controls hide on this; the backend refuses the writes either way.
 */
export function useReadOnly() {
  const { role } = useOrg();
  const supportView = getSupportView();
  return { readOnly: !!supportView || role === "viewer", supportView };
}

// Whether the signed-in person is Telcofy staff, decided by the staff endpoint
// answering at all (it refuses everyone else). Asked once per user.
let staffCache: { uid: string; promise: Promise<StaffOrganisation[] | null> } | null = null;

export function useStaffOrganisations() {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid ?? null;
  const [orgs, setOrgs] = useState<StaffOrganisation[] | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!uid) {
      setOrgs(null);
      setLoaded(true);
      return;
    }
    if (!staffCache || staffCache.uid !== uid)
      staffCache = { uid, promise: listStaffOrganisations().catch(() => null) };
    let live = true;
    staffCache.promise.then((r) => {
      if (!live) return;
      setOrgs(r);
      setLoaded(true);
    });
    return () => {
      live = false;
    };
  }, [uid]);

  return { isStaff: !!orgs, organisations: orgs ?? [], loaded };
}
